import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Download, MapPin, Mail, Phone } from 'lucide-react';
import { Section, Container, SectionHead } from './Section';
import { about, personal, stats } from '../data/portfolio';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 0.9fr 1.1fr;
    gap: 80px;
  }
`;

const PhotoWrap = styled(motion.div)`
  position: relative;
  border-radius: ${({ theme }) => theme.radii.xl};
  overflow: hidden;
  padding: 6px;
  background: linear-gradient(135deg, rgba(124,92,255,0.35), rgba(34,211,238,0.35));

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: calc(${({ theme }) => theme.radii.xl} - 6px);
    display: block;
    aspect-ratio: 4 / 5;
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const PhotoBadge = styled.div`
  position: absolute;
  left: 16px;
  bottom: 16px;
  padding: 10px 14px;
  background: rgba(11,11,18,0.7);
  backdrop-filter: blur(12px);
  border-radius: 999px;
  color: white;
  font-size: 13px;
  border: 1px solid rgba(255,255,255,0.12);
  display: inline-flex;
  align-items: center;
  gap: 8px;

  .dot { width: 8px; height: 8px; border-radius: 50%; background: #34D399; box-shadow: 0 0 10px #34D399; }
`;

const Text = styled.div`
  h3 {
    font-size: clamp(24px, 3vw, 32px);
    margin-bottom: 18px;
  }

  p {
    color: ${({ theme }) => theme.colors.textDim};
    font-size: 17px;
    margin-bottom: 24px;
    line-height: 1.75;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 30px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 18px;
  text-align: center;
  transition: transform 200ms ease, border-color 200ms ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primarySoft};
  }

  .v {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: 32px;
    font-weight: 700;
    background: linear-gradient(135deg, #A78BFA, #22D3EE);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .l { color: ${({ theme }) => theme.colors.textDim}; font-size: 13px; margin-top: 4px; }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 500;
  transition: transform 200ms ease, background 200ms ease;
  cursor: pointer;

  &:hover { transform: translateY(-2px); background: #6947f5; }
`;

const Info = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 14px;

  span { display: inline-flex; align-items: center; gap: 6px; }
`;

export default function About() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <Section id="about" ref={ref}>
      <Container>
        <SectionHead kicker="About Me" title="My" gradient="Introduction" sub="A quick look at who I am and what I bring to the table." />
        <Grid>
          <PhotoWrap initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <img src={personal.aboutImage} alt="Darryl Brown" />
            <PhotoBadge><span className="dot" /> Open to work</PhotoBadge>
          </PhotoWrap>
          <Text>
            <motion.h3 initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
              Full stack engineer with a designer's eye.
            </motion.h3>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2 }}>
              {about}
            </motion.p>
            <Stats>
              {stats.map((s, i) => (
                <Stat key={s.label} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 + i * 0.1 }}>
                  <div className="v">{s.value}</div>
                  <div className="l">{s.label}</div>
                </Stat>
              ))}
            </Stats>
            <Row>
              <DownloadBtn href={personal.resumeUrl} target="_blank" rel="noreferrer">
                <Download size={16} /> Download CV
              </DownloadBtn>
              <Info>
                <span><MapPin size={14} /> {personal.location}</span>
                <span><Mail size={14} /> {personal.email}</span>
                <span><Phone size={14} /> {personal.phone}</span>
              </Info>
            </Row>
          </Text>
        </Grid>
      </Container>
    </Section>
  );
}
